<template>
  <div id="table-pagination" class="table-pagination">
    <span
      v-if="showKeywordsTotal"
      class="total-count"
      data-cy="pagination-counter"
      v-html="`${showingConfig.from}-${showingConfig.to} of ${showingConfig.of}`"
    />
    <div class="row" v-if="!onlyTotalCount">
      <div :class="arrowClasses(currentPage !== 1)">
        <kw-button
          button-type="secondary"
          class="pg-chevron"
          prepend-icon-class="kw kw-chevron-left"
          @click="onChangePage('prev')"
        />
      </div>
      <div v-if="getPaginationConfig.separatedFirstPage">
        <kw-button
          class="separate-btn"
          :button-text="getPaginationConfig.separatedFirstPage"
          :button-type="currentPage === 1 ? 'primary' : 'secondary'"
          @click="onChangePage(getPaginationConfig.separatedFirstPage)"
        />
        <span class="separator">...</span>
      </div>
      <div v-if="getPaginationConfig.centeredConfig.length > 1" class="centered-buttons">
        <kw-button
          v-for="button in getPaginationConfig.centeredConfig"
          :key="button"
          :button-text="button"
          :button-type="currentPage === button ? 'primary' : 'secondary'"
          :data-cy="`pagination-button-${button}`"
          @click="onChangePage(button)"
        />
      </div>
      <div v-if="getPaginationConfig.separatedLastPage">
        <span class="separator">...</span>
        <kw-button
          class="separate-btn"
          :button-text="getPaginationConfig.separatedLastPage"
          :button-type="currentPage === getPaginationConfig.separatedLastPage ? 'primary' : 'secondary'"
          @click="onChangePage(getPaginationConfig.separatedLastPage)"
        />
      </div>
      <div :class="arrowClasses(currentPage !== totalPages)">
        <kw-button
          button-type="secondary"
          class="pg-chevron"
          prepend-icon-class="kw kw-chevron-right"
          @click="onChangePage('next')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
export default {
  name: 'TablePagination',
  props: {
    currentPage: {
      type: Number,
      required: true,
    },
    onChangePage: {
      type: Function,
      required: true,
    },
    perPage: {
      type: Number,
      required: true,
    },
    totalPages: {
      type: Number,
      required: true,
    },
    totalRecords: {
      type: Number,
      required: true,
    },
    onlyTotalCount: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapGetters(['showKeywordsTotal']),
    getPaginationConfig() {
      const { currentPage, perPage, totalRecords } = this;
      const lastPageNumber = parseInt(totalRecords / perPage + (totalRecords % perPage ? 1 : 0));
      let separatedFirstPage = false;
      let separatedLastPage = false;
      let centeredPagesLeft = 5;
      let centeredConfig = [];

      if (currentPage - 2 >= 1) {
        centeredConfig.push(currentPage - 2);
        centeredPagesLeft--;
      }

      if (currentPage - 1 >= 1) {
        centeredConfig.push(currentPage - 1);
        centeredPagesLeft--;
      }

      centeredConfig.push(currentPage);
      centeredPagesLeft--;

      if (currentPage + 1 <= lastPageNumber) {
        centeredConfig.push(currentPage + 1);
        centeredPagesLeft--;
      }

      if (currentPage + 2 <= lastPageNumber) {
        centeredConfig.push(currentPage + 2);
        centeredPagesLeft--;
      }

      if (centeredPagesLeft && currentPage - 3 >= 1) {
        centeredConfig.splice(0, 0, currentPage - 3);
        centeredPagesLeft--;
      }

      if (centeredPagesLeft && currentPage - 4 >= 1) {
        centeredConfig.splice(0, 0, currentPage - 4);
        centeredPagesLeft--;
      }

      if (centeredPagesLeft && currentPage + 3 <= lastPageNumber) {
        centeredConfig.push(currentPage + 3);
        centeredPagesLeft--;
      }

      if (centeredPagesLeft && currentPage + 4 <= lastPageNumber) {
        centeredConfig.push(currentPage + 4);
        centeredPagesLeft--;
      }

      if (centeredConfig[0] > 1) {
        separatedFirstPage = 1;
      }

      if (centeredConfig[4] < lastPageNumber) {
        separatedLastPage = lastPageNumber;
      }

      if (separatedFirstPage && separatedLastPage) {
        centeredConfig.splice(4, 1);
        centeredConfig.splice(0, 1);
      }

      return {
        separatedFirstPage,
        centeredConfig,
        separatedLastPage,
      };
    },
    showingConfig() {
      const { currentPage, perPage, totalRecords } = this;

      if (this.onlyTotalCount) {
        return {
          from: 1,
          to: totalRecords,
          of: totalRecords,
        };
      }

      return {
        from: (currentPage - 1) * perPage + 1,
        to: currentPage * perPage > totalRecords ? totalRecords : currentPage * perPage,
        of: totalRecords,
      };
    },
  },
  methods: {
    arrowClasses(condition) {
      return {
        arrow: true,
        arrow_hidden: !condition,
      };
    },
  },
};
</script>

<style lang="scss">
.table-pagination {
  display: flex;
  flex: 0 0 100%;
  height: 70px;
  justify-content: center;
  position: relative;

  .total-count {
    position: absolute;
    right: 0;
    line-height: 16px;
    font-size: 11px;
    color: #7b7e86;
  }

  .row {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 20px;

    .separator {
      margin-top: 5px;
      width: 40px;
      text-align: center;
    }

    button {
      width: 32px;
      height: 32px;
      padding: 0;
      margin: 0 5px;

      &.button-component_outline {
        color: kw-color(kw-blue, 1) !important;
      }
    }

    .centered-buttons {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .arrow {
      button {
        border: none;
        box-shadow: none;
        width: 15px;

        &.kw-button {
          &.pg-chevron {
            &:hover {
              border: none;
              color: kw-color(kw-blue, 1);
            }

            &:focus {
              border: none;
              color: kw-color(kw-blue, 1);
            }
          }
        }
      }

      span {
        color: #7b7e86;
        font-size: 10px;
      }
    }

    .arrow_hidden {
      opacity: 0;
      pointer-events: none;
    }

    & > div {
      display: flex;
    }
  }
}

@media (max-width: 959px) {
  .table-pagination {
    .row {
      .button-component {
        height: 25px;
        width: 25px;
      }
    }

    .total-count {
      right: 15px;
    }
  }
}
</style>
